import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";
import { toast } from "../../components/Toast";

const MBTI_LIST = [
  "INTJ","INTP","ENTJ","ENTP","INFJ","INFP","ENFJ","ENFP",
  "ISTJ","ISFJ","ESTJ","ESFJ","ISTP","ISFP","ESTP","ESFP",
];
const CATEGORIES = [
  { key: "love", label: "💕 연애" },
  { key: "friend", label: "👫 친구" },
  { key: "work", label: "💼 직장" },
];

type Participant = {
  name: string;
  birth_date: string;
  birth_time: string;
  birth_type: "solar" | "lunar";
  gender: "M" | "F";
  mbti: string;
  blood_type: string;
};

type PairResult = {
  a: number; b: number;
  a_name: string; b_name: string;
  saju_score: number; mbti_score: number; blood_score: number; total_score: number;
};

type ParticipantResult = {
  name: string; day_gan: string;
  elements: Record<string, number>;
  mbti: string | null; blood_type: string | null;
};

type GroupResult = {
  participants: ParticipantResult[];
  pairs: PairResult[];
  group_score: number;
  analysis: { overall: string; synergy: string; caution: string; advice: string };
};


function ScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? "bg-green-500" : score >= 65 ? "bg-[var(--color-accent)]" : "bg-red-500";
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 h-1.5 bg-[var(--color-border-default)] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-bold text-[var(--color-text-secondary)] w-6 text-right">{score}</span>
    </div>
  );
}

function newParticipant(): Participant {
  return { name: "", birth_date: "", birth_time: "", birth_type: "solar", gender: "M", mbti: "", blood_type: "" };
}

export default function GroupCompatibility() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  const toggleBtn = (active: boolean) =>
    `px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
      active ? "bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold" : "bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]"
    }`;

  const inputCls = "w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-3 py-2 text-[var(--color-text-primary)] text-sm focus:outline-none focus:border-[var(--color-border-accent)]";

  const [participants, setParticipants] = useState<Participant[]>([newParticipant(), newParticipant()]);
  const [saveParticipants, setSaveParticipants] = useState<boolean[]>([false, false]);
  const [partners, setPartners] = useState<any[]>([]);
  const [category, setCategory] = useState("love");
  const [result, setResult] = useState<GroupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) { navigate("/login"); return null; }

  useEffect(() => {
    if (isAuthenticated) {
      client.get('/partners')
        .then(({ data }) => setPartners(data))
        .catch((e) => console.error('파트너 목록 로딩 실패:', e));
    }
  }, [isAuthenticated]);

  const setP = (idx: number, key: keyof Participant, val: string) =>
    setParticipants((ps) => ps.map((p, i) => i === idx ? { ...p, [key]: val } : p));

  const handlePartnerSelect = (idx: number, partner: any) => {
    setParticipants((ps) => ps.map((p, i) => i === idx ? {
      name: partner.nickname,
      birth_date: partner.birth_date,
      birth_time: partner.birth_time,
      birth_type: partner.birth_type,
      gender: partner.gender,
      mbti: partner.mbti,
      blood_type: partner.blood_type,
    } : p));
  };

  const addParticipant = () => {
    if (participants.length < 5) {
      setParticipants((ps) => [...ps, newParticipant()]);
      setSaveParticipants((sps) => [...sps, false]);
    }
  };

  const removeParticipant = (idx: number) => {
    if (participants.length > 2) {
      setParticipants((ps) => ps.filter((_, i) => i !== idx));
      setSaveParticipants((sps) => sps.filter((_, i) => i !== idx));
    }
  };

  async function handleSubmit() {
    for (const p of participants) {
      if (!p.name.trim()) { setError("모든 참여자 이름을 입력해 주세요."); return; }
      if (!p.birth_date) { setError(`'${p.name}'의 생년월일을 입력해 주세요.`); return; }
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      for (let i = 0; i < participants.length; i++) {
        if (saveParticipants[i]) {
          const p = participants[i];
          await client.post('/partners', {
            nickname: p.name,
            birth_date: p.birth_date,
            birth_time: p.birth_time || null,
            birth_type: p.birth_type,
            gender: p.gender,
            mbti: p.mbti || null,
            blood_type: p.blood_type || null,
          });
        }
      }

      const res = await client.post("/features/group-compatibility", {
        participants: participants.map((p) => ({
          name: p.name.trim(),
          birth_date: p.birth_date,
          birth_time: p.birth_time || null,
          birth_type: p.birth_type,
          gender: p.gender,
          mbti: p.mbti || null,
          blood_type: p.blood_type || null,
        })),
        category,
      });
      setResult(res.data);
      toast("분석이 완료되었습니다.", "success");
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "그룹 궁합 분석에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }

  function getPairScore(result: GroupResult, i: number, j: number): number | null {
    if (i === j) return null;
    const a = Math.min(i, j), b = Math.max(i, j);
    return result.pairs.find((p) => p.a === a && p.b === b)?.total_score ?? null;
  }

  const scoreColor = (s: number) =>
    s >= 80 ? "text-green-400 bg-green-900/20" : s >= 65 ? "text-[var(--color-accent)] bg-[var(--color-accent)]/10" : "text-red-400 bg-red-900/20";

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/dashboard")} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">👥 그룹 궁합</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">2~5인 그룹 사주 궁합 분석</p>
          </div>
        </div>

        {!result ? (
          <div className="space-y-4">
            {/* 참여자 폼 */}
            {participants.map((p, idx) => (
              <div key={idx} className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-[var(--color-text-primary)]">참여자 {idx + 1}</span>
                  <div className="flex gap-2 items-center">
                    {user && (
                      <button
                        onClick={() => setParticipants((ps) => ps.map((p, i) => i === idx ? {
                          name: user.nickname,
                          birth_date: user.birth_date,
                          birth_time: user.birth_time ?? "",
                          birth_type: (user.birth_type as "solar" | "lunar") ?? "solar",
                          gender: (user.gender as "M" | "F") ?? "M",
                          mbti: user.mbti ?? "",
                          blood_type: user.blood_type ?? "",
                        } : p))}
                        className="text-xs text-[var(--color-accent)] hover:opacity-80 font-medium whitespace-nowrap"
                      >
                        내 정보
                      </button>
                    )}
                    <select
                      className="text-xs text-[var(--color-text-tertiary)] focus:outline-none cursor-pointer hover:text-[var(--color-text-primary)] bg-transparent"
                      value=""
                      onChange={(e) => {
                        const p = partners.find(item => item.id === parseInt(e.target.value))
                        if (p) handlePartnerSelect(idx, p)
                      }}
                      disabled={partners.length === 0}
                    >
                      <option value="" disabled>저장된 상대방...</option>
                      {partners.map(p => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                    </select>
                    {participants.length > 2 && (
                      <button onClick={() => removeParticipant(idx)} className="text-xs text-[var(--color-error)] hover:opacity-80">
                        삭제
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2.5">
                  <input
                    placeholder="이름 (필수)"
                    value={p.name}
                    onChange={(e) => setP(idx, "name", e.target.value)}
                    className={inputCls}
                  />

                  <div>
                    <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">생년월일 *</label>
                    <input type="date" value={p.birth_date} onChange={(e) => setP(idx, "birth_date", e.target.value)} className={inputCls} />
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">양력 / 음력</label>
                      <div className="flex gap-1">
                        <button onClick={() => setP(idx, "birth_type", "solar")} className={toggleBtn(p.birth_type === "solar")}>양력</button>
                        <button onClick={() => setP(idx, "birth_type", "lunar")} className={toggleBtn(p.birth_type === "lunar")}>음력</button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">성별</label>
                      <div className="flex gap-1">
                        <button onClick={() => setP(idx, "gender", "M")} className={toggleBtn(p.gender === "M")}>남</button>
                        <button onClick={() => setP(idx, "gender", "F")} className={toggleBtn(p.gender === "F")}>여</button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">태어난 시간 (선택)</label>
                    <input type="time" value={p.birth_time} onChange={(e) => setP(idx, "birth_time", e.target.value)} className={inputCls} />
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">MBTI</label>
                      <select value={p.mbti} onChange={(e) => setP(idx, "mbti", e.target.value)} className={inputCls}>
                        <option value="">미입력</option>
                        {MBTI_LIST.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                      <div className="flex-1">
                        <label className="block text-[10px] text-[var(--color-text-disabled)] mb-1">혈액형</label>
                        <select value={p.blood_type} onChange={(e) => setP(idx, "blood_type", e.target.value)} className={inputCls}>
                          <option value="">미입력</option>
                          {["A","B","O","AB"].map((v) => <option key={v} value={v}>{v}형</option>)}
                        </select>
                        <div className="flex items-center gap-2 pt-2">
                          <input
                            type="checkbox"
                            id={`savePartner-${idx}`}
                            className="w-4 h-4 accent-[var(--color-accent)]"
                            checked={saveParticipants[idx]}
                            onChange={(e) => setSaveParticipants((sps) => sps.map((s, i) => i === idx ? e.target.checked : s))}
                          />
                          <label htmlFor={`savePartner-${idx}`} className="text-xs text-[var(--color-text-secondary)] cursor-pointer select-none">
                            상대방 정보 저장하기
                          </label>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            ))}

            {/* 참여자 추가 */}
            {participants.length < 5 && (
              <button
                onClick={addParticipant}
                className="w-full py-2.5 border-2 border-dashed border-[var(--color-border-default)] rounded-2xl text-sm text-[var(--color-text-disabled)] hover:border-[var(--color-border-accent)] hover:text-[var(--color-accent)] transition-colors"
              >
                + 참여자 추가 ({participants.length}/5)
              </button>
            )}

            {/* 카테고리 */}
            <div>
              <p className="text-xs text-[var(--color-text-tertiary)] mb-2">궁합 카테고리</p>
              <div className="flex gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c.key} onClick={() => setCategory(c.key)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${
                      category === c.key ? "bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold" : "bg-[var(--color-surface)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)]"
                    }`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-[var(--color-error)] text-sm text-center">{error}</p>}

            <button onClick={handleSubmit} disabled={loading}
              className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-60 text-[var(--color-primary)] rounded-xl font-bold transition-opacity flex items-center justify-center gap-2">
              {loading ? <><Spinner size="sm" /> 럭키가 살펴보는 중</> : "🔮 그룹 궁합 보기"}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 그룹 종합 점수 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-6 text-center">
              <p className="text-[var(--color-text-tertiary)] text-sm mb-1">{result.participants.map((p) => p.name).join(" · ")}</p>
              <div className="text-5xl font-black text-[var(--color-accent)] mb-1">{result.group_score}점</div>
              <div className="text-[var(--color-accent)]">
                {"★".repeat(Math.round(result.group_score / 20))}
                {"☆".repeat(5 - Math.round(result.group_score / 20))}
              </div>
              <p className="text-xs text-[var(--color-text-tertiary)] mt-1">{result.participants.length}인 그룹 평균 궁합</p>
            </div>

            {/* 궁합 매트릭스 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-4">
              <h3 className="text-sm font-bold text-[var(--color-accent)] mb-3">📊 쌍별 궁합 매트릭스</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-center text-xs">
                  <thead>
                    <tr>
                      <th className="p-1 text-[var(--color-text-disabled)]"></th>
                      {result.participants.map((p, i) => (
                        <th key={i} className="p-1 font-semibold text-[var(--color-text-secondary)] max-w-[50px] truncate">{p.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.participants.map((p, i) => (
                      <tr key={i}>
                        <td className="p-1 font-semibold text-[var(--color-text-secondary)] text-left">{p.name}</td>
                        {result.participants.map((_, j) => {
                          const s = getPairScore(result, i, j);
                          return (
                            <td key={j} className="p-1">
                              {i === j ? (
                                <span className="text-[var(--color-border-default)]">—</span>
                              ) : (
                                <span className={`inline-block px-1.5 py-0.5 rounded font-bold ${scoreColor(s!)}`}>
                                  {s}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 쌍별 상세 점수 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-4">
              <h3 className="text-sm font-bold text-[var(--color-accent)] mb-3">🔍 쌍별 상세 점수</h3>
              <div className="space-y-3">
                {result.pairs.map((pair, i) => (
                  <div key={i} className="border-b border-[var(--color-border-default)] pb-3 last:border-0 last:pb-0">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                        {pair.a_name} ↔ {pair.b_name}
                      </span>
                      <span className={`text-sm font-bold px-2 py-0.5 rounded-lg ${scoreColor(pair.total_score)}`}>
                        {pair.total_score}점
                      </span>
                    </div>
                    <div className="space-y-1">
                      {[
                        { label: "오행", score: pair.saju_score },
                        { label: "MBTI", score: pair.mbti_score },
                        { label: "혈액형", score: pair.blood_score },
                      ].map(({ label, score }) => (
                        <div key={label} className="flex items-center gap-2">
                          <span className="text-[10px] text-[var(--color-text-disabled)] w-10 shrink-0">{label}</span>
                          <ScoreBar score={score} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI 분석 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5 space-y-3">
              <h3 className="text-sm font-bold text-[var(--color-accent)]">🤖 AI 그룹 분석</h3>
              <div>
                <p className="text-[10px] font-bold text-[var(--color-text-tertiary)] mb-1">📋 종합</p>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{result.analysis.overall}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-green-400 mb-1">✨ 시너지</p>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{result.analysis.synergy}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-orange-400 mb-1">⚠️ 주의</p>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{result.analysis.caution}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-blue-400 mb-1">💡 조언</p>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{result.analysis.advice}</p>
              </div>
            </div>

            <button
              onClick={() => setResult(null)}
              className="w-full py-2 text-sm text-[var(--color-text-tertiary)] underline"
            >
              다시 분석하기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
